import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'lodash';

import { buttonsV2 as Buttons, formsV2 as Forms } from '@breezeway/brzw-components';
import HeaderControlWrapper from '../base_view_control_header';

import './view_control_header.scss';

const CLASS_NAME = 'view-control-header';

const { BUTTON_SIZES, BUTTON_THEMES } = Buttons.constants;

const {
  select: { SELECT_THEMES, SELECT_ITEM_THEMES },
} = Forms.constants;

const renderActionButton = action =>
  action.icon && !action.text ? (
    <Buttons.IconButton
      iconName={action.icon}
      isLoading={action.isLoading}
      onClick={action.onClick}
      size={BUTTON_SIZES.ICON_LARGE}
      theme={action.theme}
    />
  ) : (
    <Buttons.IconTextButton
      iconNameLeft={action.iconRightSide ? '' : action.icon}
      iconNameRight={action.iconRightSide ? action.icon : ''}
      isLoading={action.isLoading}
      onClick={action.onClick}
      size={action.size ? action.size : BUTTON_SIZES.MEDIUM}
      theme={action.theme}
    >
      {action.text}
    </Buttons.IconTextButton>
  );

export default class ViewControlHeader extends Component {
  constructor(props) {
    super(props);

    this.state = {
      actions: [],
      buttonActive: false,
      inputValue: '',
      usingKeyboard: false,
    };
    this.keyDownEventName = 'keydown';
    this.bindKeyDropdownListener = this.bindKeyDropdownListener.bind(this);
    this.changeButtonState = this.changeButtonState.bind(this);
    this.handleKeyOnDropdown = this.handleKeyOnDropdown.bind(this);
    this.onExpandButtonClick = this.onExpandButtonClick.bind(this);
    this.onHeaderButtonClick = this.onHeaderButtonClick.bind(this);
    this.onOuterSelectClick = this.onOuterSelectClick.bind(this);
    this.onSearchChange = this.onSearchChange.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.unbindKeyDropdownListener = this.unbindKeyDropdownListener.bind(this);
  }

  componentDidMount() {
    if (!this.props.withFocusedItem) {
      this.bindKeyDropdownListener();
    }
  }

  componentWillUnmount() {
    this.unbindKeyDropdownListener();
  }

  onExpandButtonClick() {
    const { expand } = this.props;
    _.invoke(expand, 'onClick');
  }

  onSearchChange(value) {
    const { search } = this.props;

    this.setState({
      inputValue: value,
    });

    _.invoke(search, 'onChange', value);
  }

  onSelect(value) {
    const { select } = this.props;
    _.invoke(select, 'onSelect', value);
  }

  onHeaderButtonClick() {
    this.changeButtonState(true);
  }

  onOuterSelectClick() {
    this.changeButtonState(false);
    this.setState({ usingKeyboard: false, actions: [] });
  }

  handleKeyOnDropdown(evt) {
    const { keyCode } = evt;

    if (_.isNumber(keyCode)) {
      const actions = _.takeRight([...this.state.actions, keyCode], 2);
      this.setState({ actions });
      if (_.first(actions) === 9 && _.last(actions) === 13) {
        this.setState({ usingKeyboard: true });
      } else {
        this.setState({ usingKeyboard: false });
      }
    }
  }

  bindKeyDropdownListener() {
    document.addEventListener(this.keyDownEventName, this.handleKeyOnDropdown);
  }

  unbindKeyDropdownListener() {
    document.removeEventListener(this.keyDownEventName, this.handleKeyOnDropdown);
  }

  changeButtonState(buttonActive) {
    this.setState({
      buttonActive,
    });
  }

  renderActions() {
    const { actions, actionsClassName } = this.props;

    return (
      <div className={classnames(`${CLASS_NAME}__actions`, actionsClassName)}>
        {_.map(actions, (action, index) => (
          <div
            className={`${CLASS_NAME}__action-button`}
            key={`${CLASS_NAME}__action-button-${index}`}
          >
            {React.isValidElement(action) ? action : renderActionButton(action)}
          </div>
        ))}
      </div>
    );
  }

  renderTitle() {
    const { title, titleClassName, titleBig } = this.props;

    return (
      <div
        className={classnames(`${CLASS_NAME}__title`, titleClassName, { '-title-big': titleBig })}
      >
        {title}
      </div>
    );
  }

  renderSelect() {
    const { select, selectClassName } = this.props;
    const { buttonActive, usingKeyboard } = this.state;
    const options = _.get(select, 'options', []);
    const onSelect = _.get(select, 'onSelect');
    const withFocusedItem = usingKeyboard;
    const selectedValue = _.get(select, 'selectedValue');
    const selectedIconName = _.get(selectedValue, 'icon');
    const selectedLabel = _.get(selectedValue, 'label');
    const selectionValue = _.get(selectedValue, 'value');

    return (
      select && (
        <div className={classnames(`${CLASS_NAME}__select-wrapper`, selectClassName)}>
          {React.isValidElement(select) ? (
            select
          ) : (
            <Forms.Select
              dropdownItemTheme={SELECT_ITEM_THEMES.SECOND}
              onOuterClick={this.onOuterSelectClick}
              onSelect={onSelect}
              options={options}
              theme={SELECT_THEMES.SUBTLE}
              value={selectionValue}
              withFocusedItem={withFocusedItem}
              id="view-control-header"
            >
              <Buttons.IconTextButton
                active={buttonActive}
                iconNameLeft={selectedIconName}
                theme={BUTTON_THEMES.SUBTLE}
                onClick={this.onHeaderButtonClick}
              >
                {selectedLabel}
              </Buttons.IconTextButton>
            </Forms.Select>
          )}
        </div>
      )
    );
  }

  renderSearch() {
    const { inputValue } = this.state;
    const { search, searchClassName } = this.props;
    const placeholder = _.get(search, 'placeholder', '');

    return (
      search && (
        <div className={classnames(`${CLASS_NAME}__input-wrapper`, searchClassName)}>
          {React.isValidElement(search) ? (
            search
          ) : (
            <Forms.Input
              className={`${CLASS_NAME}__input`}
              iconBefore="search"
              onChange={this.onSearchChange}
              placeholderText={placeholder}
              size="compact"
              theme="subtle"
              value={inputValue}
              withClearAction
            />
          )}
        </div>
      )
    );
  }

  render() {
    const {
      className,
      expand,
      size,
      withBorder,
      withHorizontalMargin,
      withHorizontalPadding,
    } = this.props;
    const isExpanded = _.get(expand, 'expanded');

    return (
      <HeaderControlWrapper
        action={this.renderActions()}
        className={classnames(CLASS_NAME, className)}
        size={size}
        withBorder={withBorder}
        withHorizontalMargin={withHorizontalMargin}
        withHorizontalPadding={withHorizontalPadding}
      >
        {expand && (
          <Buttons.IconButton
            className={classnames(`${CLASS_NAME}__expand-button`, {
              [`${CLASS_NAME}__expand-button--expanded`]: isExpanded,
            })}
            iconName={isExpanded ? 'double-chevron-left' : 'double-chevron-right'}
            onClick={this.onExpandButtonClick}
            round
            theme={BUTTON_THEMES.DEFAULT}
          />
        )}
        <div className={`${CLASS_NAME}__left-container`}>
          {this.renderTitle()}
          {this.renderSelect()}
        </div>
        {this.renderSearch()}
      </HeaderControlWrapper>
    );
  }
}

ViewControlHeader.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        text: PropTypes.string,
        icon: PropTypes.string,
        onClick: PropTypes.func,
        theme: PropTypes.oneOf(_.values(BUTTON_THEMES)),
      }),
      PropTypes.node,
    ]),
  ),
  actionsClassName: PropTypes.string,
  className: PropTypes.string,
  expand: PropTypes.shape({
    expanded: PropTypes.bool,
    onClick: PropTypes.func,
  }),
  size: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  titleBig: PropTypes.bool,
  titleClassName: PropTypes.string,
  search: PropTypes.oneOfType([
    PropTypes.shape({
      placeholder: PropTypes.string,
      onChange: PropTypes.func,
      value: PropTypes.string,
    }),
    PropTypes.node,
  ]),
  searchClassName: PropTypes.string,
  select: PropTypes.oneOfType([
    PropTypes.shape({
      options: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.arrayOf(PropTypes.shape({}))]),
      onSelect: PropTypes.func,
      selectedValue: PropTypes.shape({
        icon: PropTypes.string,
        label: PropTypes.string,
      }),
    }),
    PropTypes.node,
  ]),
  selectClassName: PropTypes.string,
  withBorder: PropTypes.bool,
  withFocusedItem: PropTypes.bool,
  withHorizontalMargin: PropTypes.bool,
  withHorizontalPadding: PropTypes.bool,
};

ViewControlHeader.defaultProps = {
  withBorder: true,
};
